import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import {
  fetchPrivateWorkspaceAgentClipsListByIds,
  type PrivateWorkspaceAgentClipsListByIdsParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { isWorkspaceAgentUuid } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentUuid';
import { toWorkspaceAgentPillClipsById } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPillClipsById';

export const PRIVATE_WORKSPACE_AGENT_PILL_CLIPS_KEY = 'PRIVATE_WORKSPACE_AGENT_PILL_CLIPS';

export type PrivateWorkspaceAgentPillClipsParams = PrivateWorkspaceAgentClipsListByIdsParams;

export type PrivateWorkspaceAgentPillClipsKey = [
  typeof PRIVATE_WORKSPACE_AGENT_PILL_CLIPS_KEY,
  PrivateWorkspaceAgentPillClipsParams,
];

export const getPrivateWorkspaceAgentPillClipsKey = (
  params: PrivateWorkspaceAgentPillClipsParams,
): PrivateWorkspaceAgentPillClipsKey => [PRIVATE_WORKSPACE_AGENT_PILL_CLIPS_KEY, params];

export type UsePrivateWorkspaceAgentPillClipsOptions = {
  enabled?: boolean;
};

/**
 * Resolves the asset pills in this batch. Pills only persist ids, so this is what turns `@[pill:…]`
 * into a chip and a preview card. The fetch splits at the `listByIds` id cap: the thread shares one
 * query, and a new list is appended to ids that already resolved.
 */
export const usePrivateWorkspaceAgentPillClips = (
  params: PrivateWorkspaceAgentPillClipsParams,
  options?: UsePrivateWorkspaceAgentPillClipsOptions,
) => {
  const { clipIds, workspaceId } = params;

  // Deduped and sorted so reorders and streaming re-renders hash to the same query key.
  // `<assetClipId>` is not an id. One of those in the key still 400s the whole batch.
  const stableClipIds = [...new Set(clipIds)]
    .filter(isWorkspaceAgentUuid)
    .sort((left, right) => left.localeCompare(right));

  const keyParams = { ...params, clipIds: stableClipIds } as const;

  return useQuery({
    queryKey: getPrivateWorkspaceAgentPillClipsKey(keyParams),
    queryFn:
      workspaceId && stableClipIds.length
        ? () => fetchPrivateWorkspaceAgentClipsListByIds({ ...keyParams, workspaceId })
        : skipToken,
    enabled: !!workspaceId && stableClipIds.length > 0 && (options?.enabled ?? true),
    // A wider batch (one more pill in the thread) is a new key; without this every resolved chip
    // would flash its pending placeholder while the superset request is in flight.
    placeholderData: keepPreviousData,
    select: toWorkspaceAgentPillClipsById,
  });
};
